import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { AccountKind } from '@bits/shared/zod';
import { formatInr, paise, rupeesToPaise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const ledgerAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/accounts',
  component: LedgerAccountsPage,
});

type FormValues = {
  kind: AccountKind;
  name: string;
  numberPlaintext: string;
  openingBalanceRupees: number;
  openingAsOf: string;
};

function todayIso(): string {
  const d = new Date();
  const off = (5 * 60 + 30) * 60 * 1000;
  const ist = new Date(d.getTime() + off);
  return ist.toISOString().slice(0, 10);
}

function LedgerAccountsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  });

  const form = useForm<FormValues>({
    defaultValues: {
      kind: 'bank',
      name: '',
      numberPlaintext: '',
      openingBalanceRupees: 0,
      openingAsOf: todayIso(),
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      api.createAccount({
        kind: values.kind,
        name: values.name,
        numberPlaintext: values.numberPlaintext,
        openingBalancePaise: Number(rupeesToPaise(values.openingBalanceRupees)),
        openingAsOf: values.openingAsOf,
      }),
    onSuccess: async () => {
      setDialogOpen(false);
      setErrorMessage(null);
      form.reset({
        kind: 'bank',
        name: '',
        numberPlaintext: '',
        openingBalanceRupees: 0,
        openingAsOf: todayIso(),
      });
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateAccount(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  const rows = listQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Accounts</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Bank and cash accounts. Account numbers are masked before storage; the plaintext is discarded.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>New account</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New account</DialogTitle>
              <DialogDescription>
                Enter the full account number — the server keeps only the last 4 digits.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
              className="flex flex-col gap-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label>Kind</Label>
                  <Select {...form.register('kind')}>
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Name</Label>
                  <Input {...form.register('name', { required: true })} placeholder="HDFC — Current" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Account number (plaintext)</Label>
                  <Input {...form.register('numberPlaintext', { required: true })} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Opening balance (INR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    {...form.register('openingBalanceRupees', { valueAsNumber: true })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Opening as of</Label>
                  <Input type="date" {...form.register('openingAsOf', { required: true })} />
                </div>
              </div>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Saving…' : 'Create account'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All accounts</CardTitle>
          <CardDescription>{rows.length} total</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No accounts yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((a) => (
                <li
                  key={a.id}
                  className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{a.name}</div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
                      <Badge variant="muted">{a.kind}</Badge>
                      <span>{a.numberMasked}</span>
                      <span>· opening {formatInr(paise(a.openingBalancePaise))}</span>
                      {!a.active ? <Badge variant="destructive">inactive</Badge> : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center justify-between gap-3">
                    <div className="sm:text-right">
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">balance</div>
                      <div className="font-medium">
                        {formatInr(paise(a.currentBalancePaise))}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!a.active || deactivateMutation.isPending}
                      onClick={() => deactivateMutation.mutate(a.id)}
                    >
                      {a.active ? 'Deactivate' : 'Inactive'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
